import { useCallback, useMemo, useRef, useState } from 'react';
import { Responsive, useContainerWidth } from 'react-grid-layout'; // Added useContainerWidth
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@sendible/design-system';
import { useReportingContext, getMinH } from '../../../../context';
import type { GridLayoutItem } from '../../../../context';
import { ModuleShell } from '../../..';
import {
  ModuleGridArea,
  ModuleGridInner,
  ModuleGridContainer,
  EmptyReportState,
  EmptyStateCard,
  EmptyStateInnerBox,
  EmptyStateTitle,
  EmptyStateSubtitle,
  ModuleGridItem,
} from '../../index.styles';
import { GhostCellOverlay } from '../GhostCellOverlay';
import type { GhostCellOverlayHandle } from '../GhostCellOverlay';

const ROW_HEIGHT = 140;
const GRID_COLS = { lg: 4, md: 4, sm: 2, xs: 1, xxs: 1 };
const GRID_MARGIN: [number, number] = [16, 16];

type LayoutItem = { i: string; x: number; y: number; w: number; h: number };

type ModuleGridProps = {
  isEditing: boolean;
  moduleDefinitions: ModuleDefinition[] | undefined;
  onRemoveModule: (reportModuleId: string) => void;
  onOpenAddPanel?: () => void;
};

export const ModuleGrid = ({ isEditing, moduleDefinitions, onRemoveModule, onOpenAddPanel }: ModuleGridProps) => {
  const { t } = useTranslation('reports');
  const { modules, gridLayout, updateGridLayout } = useReportingContext();

  // ── Hook-based Width Tracking ──
  // This automatically tracks container size and replaces manual ResizeObserver hooks/states
  const {
    width: containerWidth,
    containerRef,
    mounted,
  } = useContainerWidth({
    initialWidth: 1200,
  });

  const isRemovingRef = useRef(false);
  const overlayRef = useRef<GhostCellOverlayHandle>(null);
  const isDraggingRef = useRef(false);

  const [isDragging, setIsDragging] = useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');

  const currentCols = GRID_COLS[currentBreakpoint as keyof typeof GRID_COLS] || 4;

  const totalRows = useMemo(() => {
    if (gridLayout.length === 0) return 0;

    const maxRow = Math.max(...gridLayout.map((item) => item.y + item.h));

    return maxRow + 2;
  }, [gridLayout]);

  // Handle Drag/Resize handlers (no longer need to manually sample offsets from the DOM)
  const handleDragStart = useCallback((_layout: LayoutItem[], _oldItem: LayoutItem, newItem: LayoutItem) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    overlayRef.current?.update({ x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h });
  }, []);

  const handleDrag = useCallback((_layout: LayoutItem[], _oldItem: LayoutItem, newItem: LayoutItem) => {
    overlayRef.current?.update({ x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h });
  }, []);

  const handleDragStop = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleResizeStart = useCallback((_layout: LayoutItem[], _oldItem: LayoutItem, newItem: LayoutItem) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    overlayRef.current?.update({ x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h });
  }, []);

  const handleResize = useCallback((_layout: LayoutItem[], _oldItem: LayoutItem, newItem: LayoutItem) => {
    overlayRef.current?.update({ x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h });
  }, []);

  const handleResizeStop = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleRemoveModule = useCallback(
    (reportModuleId: string) => {
      isRemovingRef.current = true;
      onRemoveModule(reportModuleId);
      requestAnimationFrame(() => {
        isRemovingRef.current = false;
      });
    },
    [onRemoveModule]
  );

  const handleBreakpointChange = useCallback((newBreakpoint: string) => {
    setCurrentBreakpoint(newBreakpoint);
  }, []);

  const handleLayoutChange = useCallback(
    (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
      if (isRemovingRef.current || isDraggingRef.current) return;
      if (currentBreakpoint !== 'lg' && currentBreakpoint !== 'md') return;

      const currentModuleIds = new Set(modules.map((m) => m.reportModuleId));
      const filtered = layout.filter((item) => currentModuleIds.has(item.i));

      const mapped: GridLayoutItem[] = filtered.map((item) => {
        const mod = modules.find((m) => m.reportModuleId === item.i);
        const moduleDefinition = mod ? moduleDefinitions?.find((definition) => definition.id === mod.moduleDefinitionId) : undefined;
        const minW = moduleDefinition?.minSize?.width ?? 1;
        const minH = mod ? getMinH(mod.moduleType, moduleDefinition) : 2;
        const maxW = moduleDefinition?.maxSize?.width ?? Infinity;
        const maxH = moduleDefinition?.maxSize?.height ?? Infinity;

        return {
          i: item.i,
          x: item.x,
          y: item.y,
          w: Math.min(Math.max(item.w, minW), maxW),
          h: Math.min(Math.max(item.h, minH), maxH),
          minW,
          minH,
          maxW: moduleDefinition?.maxSize?.width,
          maxH: moduleDefinition?.maxSize?.height,
          isResizable: moduleDefinition?.isResizable ?? true,
        };
      });

      updateGridLayout(mapped);
    },
    [updateGridLayout, modules, moduleDefinitions, currentBreakpoint]
  );

  const activeLayouts = useMemo(() => {
    const sanitizedLayout = gridLayout.map((item) => ({
      ...item,
      isDraggable: isEditing,
      isResizable: isEditing ? item.isResizable : false,
    }));

    return {
      lg: sanitizedLayout,
      md: sanitizedLayout, // keeps your sidebar breakpoint safe too!
    };
  }, [gridLayout, isEditing]);

  if (modules.length === 0) {
    return (
      <ModuleGridArea>
        <ModuleGridInner ref={containerRef}>
          <EmptyReportState>
            <EmptyStateCard>
              <EmptyStateInnerBox>
                <div>
                  <Icon
                    color="brandPrimaryTint50"
                    name="plus"
                  />
                </div>
              </EmptyStateInnerBox>
              <EmptyStateTitle>{t('new_reports_empty_modules_title')}</EmptyStateTitle>
              <EmptyStateSubtitle>{t('new_reports_empty_modules_subtitle')}</EmptyStateSubtitle>
              {onOpenAddPanel && (
                <Button
                  appearance="primary"
                  variation="fill"
                  size={12}
                  label={t('new_reports_empty_modules_button') as string}
                  onClick={onOpenAddPanel}
                />
              )}
            </EmptyStateCard>
          </EmptyReportState>
        </ModuleGridInner>
      </ModuleGridArea>
    );
  }

  return (
    <ModuleGridArea>
      <ModuleGridInner ref={containerRef}>
        <ModuleGridContainer
          ref={containerRef}
          $isDragging={isDragging}
        >
          {isEditing && isDragging && containerWidth > 0 && (
            <GhostCellOverlay
              ref={overlayRef}
              cols={currentCols}
              rowHeight={ROW_HEIGHT}
              margin={GRID_MARGIN}
              containerWidth={containerWidth}
              totalRows={totalRows}
            />
          )}
          {mounted && (
            <Responsive
              layouts={activeLayouts}
              breakpoints={{ lg: 1024, md: 768, sm: 480, xs: 320, xxs: 0 }}
              cols={GRID_COLS}
              width={containerWidth}
              rowHeight={ROW_HEIGHT}
              margin={GRID_MARGIN}
              containerPadding={[0, 0]}
              onLayoutChange={handleLayoutChange}
              onBreakpointChange={handleBreakpointChange}
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onDragStop={handleDragStop}
              onResizeStart={handleResizeStart}
              onResize={handleResize}
              onResizeStop={handleResizeStop}
              isDraggable={isEditing}
              isResizable={isEditing}
              draggableHandle=".module-drag-handle"
              useCSSTransforms
              compactType="vertical"
            >
              {modules.map((mod) => {
                const definition = moduleDefinitions?.find((d) => d.id === mod.moduleDefinitionId);
                const isModuleResizable = definition?.isResizable ?? true;

                return (
                  <ModuleGridItem
                    key={mod.reportModuleId}
                    data-module-id={mod.reportModuleId}
                    data-resizable={isEditing && isModuleResizable ? 'true' : 'false'}
                  >
                    <ModuleShell
                      module={mod}
                      moduleDefinition={definition}
                      onRemove={isEditing ? handleRemoveModule : undefined}
                      isEditing={isEditing}
                    />
                  </ModuleGridItem>
                );
              })}
            </Responsive>
          )}
        </ModuleGridContainer>
      </ModuleGridInner>
    </ModuleGridArea>
  );
};
